import { useMutation, useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useDispatch } from 'react-redux';
import { useReviewCreationData, setFormFillings, useFormFillings } from 'reducers/reviewCreationSlice';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useReviewCreationNavigation } from '../useReviewCreationNavigation';

export interface FormFilling {
  feedbackFormId: number;
  feedbackFormName?: string;
  cohort: Cohort;
  formFillingScoreSettings: FormFillingScoreSettings;
  enabledProviderTypes: string[];
  providerTypeToTemplateMap: ProviderTypeToTemplateMap[];
  settingSameAsFormId: number;
  isAllowSameSettingAsFirstView: boolean;
}

export interface Cohort {
  id: number;
  name: string;
}

export interface FormFillingScoreSettings {
  selfFormFillingScoreSettings: SelfFormFillingScoreSettings;
  managerFormFillingScoreSettings: ManagerFormFillingScoreSettings;
  peerFormFillingScoreSettings: PeerFormFillingScoreSettings;
}

export interface SelfFormFillingScoreSettings {
  isShowGoalRating: boolean;
  isShowCompetencyRating: boolean;
  isShowOverallRating: boolean;
}

export interface ManagerFormFillingScoreSettings {
  isShowGoalRating: boolean;
}

export const SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES = {
  GOAL: 'GOAL',
  COMPETENCY: 'COMPETENCY',
  QNA: 'QNA',
} as const;

export const SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES_LABELS = {
  [SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES.GOAL]: 'Goals',
  [SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES.COMPETENCY]: 'Competency',
  [SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES.QNA]: 'Q&A',
} as const;

export interface PeerFormFillingScoreSettings {
  selfAnswerVisibilityForPeers: (typeof SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES)[keyof typeof SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES][];
}

export interface ProviderTypeToTemplateMap {
  template?: Template;
  providerType: string;
}

export interface Template {
  id: number;
  created: string;
  name: string;
  formType: string;
  createdByEmployeeId: number;
  isArchived: boolean;
  questionCount: number;
  templateSetting: TemplateSetting;
  createdByEmployee: object;
  isSample: boolean;
  ongoingReviewCycleCount: number;
  // createdByEmployeeMiniProfile: object;
}

export interface TemplateSetting {
  id: number;
  templateId: number;
  overallRatingDistribution: OverallRatingDistribution;
  isEnableGoalLevelQuestion: boolean;
  carryForwardAgenda: boolean;
}

export interface OverallRatingDistribution {
  PERFORMANCE: number;
  POTENTIAL: number;
  QNA: number;
}

const getFormFillings = ({
  reviewCycleId,
}: {
  reviewCycleId: string;
}): Promise<{ entity: { formFillingSettings: FormFilling[] } }> => {
  // return Promise.resolve({
  //   entity: {
  //     formFillingSettings: [
  //       {
  //         feedbackFormId: 4953,
  //         cohort: {
  //           id: 732,
  //           name: 'ABCDNEERAJ (Copy)'
  //         },
  //         isAllowSameSettingAsFirstView: false,
  //         formFillingSettings: {
  //           selfFormFillingScoreSettings: {
  //             isShowGoalRating: true,
  //             isShowCompetencyRating: true,
  //             isShowOverallRating: true
  //           },
  //           managerFormFillingScoreSettings: {
  //             isShowGoalRating: true
  //           },
  //           peerFormFillingScoreSettings: {
  //             isShowSelfAnswers: false
  //           }
  //         },
  //         enabledProviderTypes: ['SELF', 'PEER', 'MANAGER', 'UPWARD'],
  //         providerTypeToTemplateMap: [
  //           {
  //             template: {
  //               id: 5873,
  //               created: '2023-02-01T18:55:38.000+05:30',
  //               name: 'Individual Goals',
  //               formType: 'REVIEW',
  //               createdByEmployeeId: 14804,
  //               isArchived: false,
  //               questionCount: 0,
  //               templateSetting: {
  //                 id: 2074,
  //                 templateId: 5873,
  //                 overallRatingDistribution: {
  //                   PERFORMANCE: 40.0,
  //                   POTENTIAL: 40.0,
  //                   QNA: 20.0
  //                 },
  //                 isEnableGoalLevelQuestion: true,
  //                 carryForwardAgenda: false
  //               },
  //               createdByEmployee: null,
  //               isSample: null,
  //               ongoingReviewCycleCount: 0,
  //               createdByEmployeeMiniProfile: null
  //             },
  //             providerType: 'SELF'
  //           },
  //           {
  //             template: {
  //               id: 5873,
  //               created: '2023-02-01T18:55:38.000+05:30',
  //               name: 'Individual Goals',
  //               formType: 'REVIEW',
  //               createdByEmployeeId: 14804,
  //               isArchived: false,
  //               questionCount: 0,
  //               templateSetting: {
  //                 id: 2074,
  //                 templateId: 5873,
  //                 overallRatingDistribution: {
  //                   PERFORMANCE: 40.0,
  //                   POTENTIAL: 40.0,
  //                   QNA: 20.0
  //                 },
  //                 isEnableGoalLevelQuestion: true,
  //                 carryForwardAgenda: false
  //               },
  //               createdByEmployee: null,
  //               isSample: null,
  //               ongoingReviewCycleCount: 0,
  //               createdByEmployeeMiniProfile: null
  //             },
  //             providerType: 'PEER'
  //           },
  //           {
  //             template: {
  //               id: 5873,
  //               created: '2023-02-01T18:55:38.000+05:30',
  //               name: 'Individual Goals',
  //               formType: 'REVIEW',
  //               createdByEmployeeId: 14804,
  //               isArchived: false,
  //               questionCount: 0,
  //               templateSetting: {
  //                 id: 2074,
  //                 templateId: 5873,
  //                 overallRatingDistribution: {
  //                   PERFORMANCE: 40.0,
  //                   POTENTIAL: 40.0,
  //                   QNA: 20.0
  //                 },
  //                 isEnableGoalLevelQuestion: true,
  //                 carryForwardAgenda: false
  //               },
  //               createdByEmployee: null,
  //               isSample: null,
  //               ongoingReviewCycleCount: 0,
  //               createdByEmployeeMiniProfile: null
  //             },
  //             providerType: 'MANAGER'
  //           },
  //           {
  //             template: null,
  //             providerType: 'UPWARD'
  //           }
  //         ],
  //         settingSameAsFormId: null
  //       }
  //     ]
  //   }
  // });
  const url = endpoints.reviews.reviewCycleCreation.formFilling;
  return sendRequest({
    url,
    credentials: 'include',
    queryParams: {
      review_cycle_id: reviewCycleId,
    },
  });
};

const putFormFillings = ({
  reviewCycleId,
  formFillingSettings,
}: {
  reviewCycleId: string;
  formFillingSettings: FormFilling[];
}) => {
  const url = endpoints.reviews.reviewCycleCreation.formFilling;
  return sendRequest({
    url,
    method: 'PUT',
    credentials: 'include',
    queryParams: {
      review_cycle_id: reviewCycleId,
    },
    body: {
      formFillingSettings,
    },
  });
};

export const useGetFormFillings = ({ reviewCycleId }: { reviewCycleId: string }) => {
  const dispatch = useDispatch();
  return useQuery(
    [REACT_QUERY_KEYS.reviewCreation.FORM_FILLING_EXPERIENCE, reviewCycleId],
    () =>
      getFormFillings({ reviewCycleId }).then((res) => {
        const { formFillingSettings } = res.entity;
        dispatch(setFormFillings(formFillingSettings));
        return res.entity;
      }),
    { enabled: !!reviewCycleId }
  );
};

export const useSaveFormFillings = ({ isPreviewClick = false } = {}) => {
  const { goToNextStep, goToStep } = useReviewCreationNavigation();
  const formFillingSettings: FormFilling[] = useFormFillings();
  const { reviewCycleId } = useReviewCreationData();

  // TODO: v1 or later
  // const settingsForFeedbackFormIdsMap = new Map<number, FormFilling>();
  // formFillingSettings.forEach((setting) => {
  //   settingsForFeedbackFormIdsMap.set(setting.feedbackFormId, setting);
  // });

  // const copiedSettingsFromViewIds = formFillingSettings.map((setting) => {
  //   if (
  //     !setting.isAllowSameSettingAsFirstView ||
  //     setting.settingSameAsFormId === null ||
  //     !settingsForFeedbackFormIdsMap.has(setting.settingSameAsFormId)
  //   ) {
  //     return setting;
  //   }
  //   const copiedSetting = settingsForFeedbackFormIdsMap.get(
  //     setting.settingSameAsFormId
  //   ).formFillingScoreSettings;
  //   return { ...setting, formFillingScoreSettings: copiedSetting };
  // });

  return useMutation({
    mutationFn: () =>
      putFormFillings({
        reviewCycleId,
        formFillingSettings,
      }),
    onSuccess: (_data, variables: { nextStep?: string }) => {
      if (variables?.nextStep) {
        goToStep(variables.nextStep);
        return;
      }
      if (isPreviewClick) return;
      goToNextStep();
    },
    onError: () => {},
  });
};

export const useTemplateMetadata = ({
  reviewCycleId,
  feedbackFormId,
  providerType,
}: {
  reviewCycleId: string;
  feedbackFormId: number;
  providerType: string;
}) => {
  const { data: { formFillingSettings } = {} } = useGetFormFillings({ reviewCycleId });

  if (!formFillingSettings) return null;

  const metaDataForViewId = formFillingSettings.find((f) => f.feedbackFormId === feedbackFormId);
  return metaDataForViewId?.providerTypeToTemplateMap?.find((m) => m.providerType === providerType);
};
